import axios from 'axios';
import { authUtils } from './auth';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3101';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests automatically
api.interceptors.request.use(
  (config) => {
    const token = authUtils.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, clear auth data
      authUtils.logout();
      // Redirect to login if not already there
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/admin')) {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

// Research Papers API
export const researchApi = {
  getAll: (params?: { skip?: number; limit?: number; year?: number; topic?: string }) =>
    api.get('/research/', { params }),
  getById: (id: number) => api.get(`/research/${id}`),
  search: (query: string) => api.get('/research/search/', { params: { query } }),
  create: (data: FormData) => api.post('/research/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id: string, data: FormData) => api.put(`/research/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id: string) => api.delete(`/research/${id}`),
};

// Blogs API
export const blogsApi = {
  getAll: (params?: { 
    skip?: number; 
    limit?: number; 
    is_active?: boolean;
    search?: string;
    tag?: string;
    category_id?: string;
    start_date?: string;
    end_date?: string;
    lang?: string;
  }) =>
    api.get('/blogs/', { params }),
  getById: (id: string, lang?: string) => api.get(`/blogs/${id}`, { params: { lang } }),
  getBySlug: (slug: string, lang?: string) => api.get(`/blogs/slug/${slug}`, { params: { lang } }),
  create: (data: FormData) => api.post('/blogs/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id: string, data: FormData) => api.put(`/blogs/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id: string) => api.delete(`/blogs/${id}`),
  updateTranslation: (id: string, languageCode: string, data: FormData) => api.put(`/blogs/${id}/translations/${languageCode}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// Careers API
export const careersApi = {
  getAll: (params?: { skip?: number; limit?: number; is_active?: boolean }) =>
    api.get('/careers/', { params }),
  getById: (id: number) => api.get(`/careers/${id}`),
};

// Chat API
export const chatApi = {
  sendMessage: (message: string, userId?: string, sessionId?: string, language?: string) =>
    api.post('/chat/', { message, user_id: userId, session_id: sessionId, language: language || 'en' }),
  getHistory: (sessionId: string) => api.get(`/chat/history/${sessionId}`),
  healthCheck: () => api.get('/chat/health'),
};

// Auth API
export const authApi = {
  login: (email: string, password: string) => {
    const formData = new URLSearchParams();
    formData.append('grant_type', 'password');
    formData.append('username', email);
    formData.append('password', password);
    formData.append('scope', '');
    formData.append('client_id', 'string');
    formData.append('client_secret', 'string');
    
    return api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
};

// Employee API
export const employeeApi = {
  getAll: (params?: { skip?: number; limit?: number; is_active?: boolean; lang?: string }) =>
    api.get('/employees/', { params }),
  getById: (id: string, lang?: string) => api.get(`/employees/${id}`, { params: { lang } }),
  create: (data: FormData) => api.post('/employees/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id: string, data: FormData) => api.put(`/employees/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id: string) => api.delete(`/employees/${id}`),
  getTranslation: (id: string, languageCode: string) => 
    api.get(`/employees/${id}`, { params: { lang: languageCode } }),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/employees/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

// Products API
export const productsApi = {
  getAll: (params?: { skip?: number; limit?: number; id_category?: string; is_active?: boolean; lang?: string }) =>
    api.get('/products/', { params }),
  getById: (id: string, lang?: string) => api.get(`/products/${id}`, { params: { lang } }),
  getBySlug: (slug: string, lang?: string) => api.get(`/products/slug/${slug}`, { params: { lang } }),
  create: (data: FormData) => api.post('/products/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id: string, data: FormData) => api.put(`/products/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/products/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: string) => api.delete(`/products/${id}`),
};

// Clients API
export const clientsApi = {
  getAll: (params?: { skip?: number; limit?: number; industry?: string; is_active?: boolean; lang?: string }) =>
    api.get('/clients/', { params }),
  getById: (id: string, lang?: string) => api.get(`/clients/${id}`, { params: { lang } }),
  create: (data: FormData) => api.post('/clients/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id: string, data: FormData) => api.put(`/clients/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id: string) => api.delete(`/clients/${id}`),
  getTranslation: (id: string, languageCode: string) =>
    api.get(`/clients/${id}/translations/${languageCode}`),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/clients/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

// Teams API (matches openapi.json TeamCreate/TeamUpdate schemas)
export const teamsApi = {
  getAll: (params?: { skip?: number; limit?: number }) =>
    api.get('/teams/', { params }),
  getById: (id: string) => api.get(`/teams/${id}`),
  create: (data: { name_team: string; employee_ids?: string[] }) => 
    api.post('/teams/', data),
  update: (id: string, data: { name_team?: string; employee_ids?: string[] }) => 
    api.put(`/teams/${id}`, data),
  delete: (id: string) => api.delete(`/teams/${id}`),
};

// Research API (Full CRUD) - Uses FormData for file uploads
export const researchPapersApi = {
  getAll: (params?: { skip?: number; limit?: number; category?: string; is_published?: boolean; lang?: string }) =>
    api.get('/research/', { params }),
  getById: (id: string, lang?: string) => api.get(`/research/${id}`, { params: { lang } }),
  getBySlug: (slug: string, lang?: string) => api.get(`/research/slug/${slug}`, { params: { lang } }),
  create: (data: FormData) => api.post('/research/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id: string, data: FormData) => api.put(`/research/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/research/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: string) => api.delete(`/research/${id}`),
};

// Careers API (Full CRUD) - matches openapi.json CareerCreate/CareerUpdate
export const careerPositionsApi = {
  getAll: (params?: { skip?: number; limit?: number; department?: string; type?: string; is_active?: boolean; lang?: string }) =>
    api.get('/careers/', { params }),
  getById: (id: string, lang?: string) => api.get(`/careers/${id}`, { params: { lang } }),
  create: (data: {
    title: string;
    email?: string | null;
    id_department?: string | null;
    department?: string | null;
    location?: string | null;
    type?: string | null;
    description?: string | null;
    requirements?: string | null;
    responsibilities?: string | null;
    qualifications?: string | null;
    salary?: string | null;
    benefits?: string | null;
    application_deadline?: string | null;
    posted_at?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    is_active?: boolean;
    is_published?: boolean;
  }) => api.post('/careers/', data),
  update: (id: string, data: {
    title?: string | null;
    email?: string | null;
    id_department?: string | null;
    department?: string | null;
    location?: string | null;
    type?: string | null;
    description?: string | null;
    requirements?: string | null;
    responsibilities?: string | null;
    qualifications?: string | null;
    salary?: string | null;
    benefits?: string | null;
    application_deadline?: string | null;
    posted_at?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    is_active?: boolean | null;
    is_published?: boolean | null;
  }) => api.put(`/careers/${id}`, data),
  getTranslation: (id: string, languageCode: string) =>
    api.get(`/careers/${id}/translations/${languageCode}`),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/careers/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: string) => api.delete(`/careers/${id}`),
  // Valid career types per spec (lowercase with spaces)
  CAREER_TYPES: ['full time', 'remote', 'intern', 'part time'] as const,
};

// Chatbot Config API - uses PATCH per openapi.json
export const chatbotConfigApi = {
  get: () => api.get('/admin/chatbot/config'),
  update: (data: {
    system_prompt?: string | null;
    temperature?: number | null;
    model_top_p?: number | null;
    model_top_k?: number | null;
    retrieval_top_k?: number | null;
    max_token?: number | null;
    max_messages_per_user?: number | null;
    max_total_users?: number | null;
    max_message_length?: number | null;
    max_answer_length?: number | null;
    model_name?: string | null;
  }) => api.patch('/admin/chatbot/config', data),
  getModels: () => api.get('/admin/chatbot/models'),
  syncEmbeddings: (data: { table_name: string; record_ids?: number[] | null; force_reindex?: boolean }) =>
    api.post('/admin/chatbot/sync-embeddings', data),
};

// Users (Admin Accounts) API
export const usersApi = {
  getAll: (params?: { skip?: number; limit?: number }) =>
    api.get('/users/', { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: { 
    email: string; 
    password: string; 
    role: 'admin' | 'blog-admin' | 'research-admin' | 'career-admin'; 
    is_active?: boolean 
  }) => api.post('/users/', data),
  update: (id: string, data: { 
    email?: string; 
    role?: 'admin' | 'blog-admin' | 'research-admin' | 'career-admin'; 
    is_active?: boolean 
  }) => api.put(`/users/${id}`, data),
  updatePassword: (id: string, data: { 
    current_password: string; 
    new_password: string 
  }) => api.put(`/users/${id}/password`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

// Dashboard Stats API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// ==========================================
// NEW LOOKUP TABLE APIs
// ==========================================

// Position Employee Lookup API
export const positionEmployeeApi = {
  getAll: (params?: { skip?: number; limit?: number; search?: string }) =>
    api.get('/position-employees/', { params }),
  getById: (id: string) => api.get(`/position-employees/${id}`),
  create: (data: { name_position: string }) => api.post('/position-employees/', data),
  update: (id: string, data: { name_position?: string }) => api.put(`/position-employees/${id}`, data),
  delete: (id: string) => api.delete(`/position-employees/${id}`),
};

// Category Employee Lookup API
export const categoryEmployeeApi = {
  getAll: (params?: { skip?: number; limit?: number; search?: string; lang?: string }) =>
    api.get('/category-employees/', { params }),
  getById: (id: string, lang?: string) => api.get(`/category-employees/${id}`, { params: { lang } }),
  create: (data: { name_category: string }) => api.post('/category-employees/', data),
  update: (id: string, data: { name_category?: string }) => api.put(`/category-employees/${id}`, data),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/category-employees/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: string) => api.delete(`/category-employees/${id}`),
};

// Category Lookup API (for Blog, Product, Research)
export const categoryApi = {
  getAll: (params?: { skip?: number; limit?: number; search?: string; lang?: string }) =>
    api.get('/categories/', { params }),
  getById: (id: string, lang?: string) => api.get(`/categories/${id}`, { params: { lang } }),
  create: (data: { name_category: string }) => api.post('/categories/', data),
  update: (id: string, data: { name_category?: string }) => api.put(`/categories/${id}`, data),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/categories/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: string) => api.delete(`/categories/${id}`),
};

// Department Lookup API (for Career)
export const departmentApi = {
  getAll: (params?: { skip?: number; limit?: number; search?: string }) =>
    api.get('/departments/', { params }),
  getById: (id: string) => api.get(`/departments/${id}`),
  create: (data: { name_department: string }) => api.post('/departments/', data),
  update: (id: string, data: { name_department?: string }) => api.put(`/departments/${id}`, data),
  delete: (id: string) => api.delete(`/departments/${id}`),
};

// Guides & Tutorials API (supports file upload)
export const guidesApi = {
  getAll: (params?: { skip?: number; limit?: number; search?: string }) =>
    api.get('/guides/', { params }),
  getById: (id: string, lang?: string) => api.get(`/guides/${id}`, { params: { lang } }),
  // Create with file upload (multipart/form-data)
  create: (data: FormData) => api.post('/guides/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  // Create with JSON (external link only)
  createJson: (data: { guides_title: string; guides_description?: string; pdf_link: string }) =>
    api.post('/guides/json', data),
  // Update with file upload (multipart/form-data)
  update: (id: string, data: FormData) => api.put(`/guides/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  // Update with JSON (external link only)
  updateJson: (id: string, data: { guides_title?: string; guides_description?: string; pdf_link?: string; clear_url?: boolean }) =>
    api.put(`/guides/${id}/json`, data),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/guides/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: string) => api.delete(`/guides/${id}`),
};

// Content Information API
export const contentInfoApi = {
  getAll: (params?: { skip?: number; limit?: number; type?: string; lang?: string }) =>
    api.get('/content-info/', { params }),
  getById: (id: string, lang?: string) => api.get(`/content-info/${id}`, { params: { lang } }),
  getByIdFull: (id: string, lang?: string) => api.get(`/content-info/${id}/full`, { params: { lang } }),
  create: (data: { content_title: string; content_description?: string; type?: string }) =>
    api.post('/content-info/', data),
  update: (id: string, data: { content_title?: string; content_description?: string; type?: string }) =>
    api.put(`/content-info/${id}`, data),
  delete: (id: string) => api.delete(`/content-info/${id}`),
  updateTranslation: (id: string, languageCode: string, data: { content_title?: string; content_description?: string }) =>
    api.put(`/content-info/${id}/translations/${languageCode}`, data),
  // Valid content types per API
  CONTENT_TYPES: ['hero-1', 'hero-2', 'hero-3', 'hero-4', 'solution', 'research', 'blog', 'career'] as const,
};

// Cards API
export const cardsApi = {
  getAll: (params?: { skip?: number; limit?: number; id_content?: string; lang?: string }) =>
    api.get('/cards/', { params }),
  getById: (id: string, lang?: string) => api.get(`/cards/${id}`, { params: { lang } }),
  getByIdFull: (id: string, lang?: string) => api.get(`/cards/${id}/full`, { params: { lang } }),
  create: (data: FormData) => api.post('/cards/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id: string, data: FormData) => api.put(`/cards/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id: string) => api.delete(`/cards/${id}`),
  updateTranslation: (id: string, languageCode: string, data: FormData) => api.put(`/cards/${id}/translations/${languageCode}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// Web Information API
export const webInfoApi = {
  getAll: (params?: { skip?: number; limit?: number; lang?: string }) =>
    api.get('/web-info/', { params }),
  getById: (id: string, lang?: string) => api.get(`/web-info/${id}`, { params: { lang } }),
  create: (data: {
    email?: string;
    phone?: string;
    office_address?: string;
    office_address_url?: string;
    terms_of_service?: string;
    privacy_policy?: string;
  }) => api.post('/web-info/', data),
  update: (id: string, data: {
    email?: string;
    phone?: string;
    office_address?: string;
    office_address_url?: string;
    terms_of_service?: string;
    privacy_policy?: string;
  }) => api.put(`/web-info/${id}`, data),
  getTranslation: (id: string, languageCode: string) =>
    api.get(`/web-info/${id}/translations/${languageCode}`),
  updateTranslation: (id: string, languageCode: string, data: FormData) =>
    api.put(`/web-info/${id}/translations/${languageCode}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: string) => api.delete(`/web-info/${id}`),
};

export default api;